import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { CheckConfirmCircleIcon, QuestionCircleIcon, WarningCircleIcon } from '@/lib/icon';
import { Flex } from '@/lib/v2/components/Layout/Stack';
import Select, { Option } from '@/lib/v2/components/Select/Select';

import { isValidFormat } from '@/src/application/util/validators';

import { ITableMappingField } from './ContactsMappingField';
import { SelectorEnum } from './selectorsValues';

import { StateOption } from '@/modules/ContactsModule/screens/ImportMain/StateOptionsEnum.enum';

interface SelectRowProps {
  value: ITableMappingField;
  listCustomField: Option[];
  onChange: (option: Option, id: string | number, selectIndex: number) => void;
  onchangeDataType: (option: Option, id: string | number) => void;
  id: string | number;
  option: Option;
  selectDataType: Option[] | undefined;
  selected: Option;
  isInvalid: boolean;
  selectIndex: number;
}

const WarningDataMatch = ({
  data,
  format,
  dataType,
  id,
  alreadyInUse,
}: {
  data: string[];
  format: string;
  dataType: string;
  id: string | number;
  alreadyInUse: boolean;
}) => {
  let warningIcon = false;

  if (dataType === SelectorEnum.date || dataType === SelectorEnum.sex) {
    warningIcon = data.some((element: string) => !isValidFormat(element, format, dataType));
  }

  if (id === StateOption.noImport && !alreadyInUse) {
    return <QuestionCircleIcon />;
  } else if (warningIcon || (alreadyInUse && id === StateOption.noImport)) {
    return <WarningCircleIcon />;
  } else {
    return <CheckConfirmCircleIcon green />;
  }
};

function SelectRow({
  value,
  listCustomField,
  onChange,
  onchangeDataType,
  selectIndex,
  id,
  option,
  selectDataType,
  selected,
  isInvalid,
}: SelectRowProps) {
  const { t } = useTranslation();

  const handleOnChange = (e: Option | Option[]) => {
    onChange(e as Option, id, selectIndex);
  };

  const handleDataType = (e: Option | Option[]) => {
    onchangeDataType(e as Option, id);
  };
  const selectMultiple = selectDataType && id !== -1;
  const format = value.formatColumn?.selected?.id.toString();
  const dataType = value.formatColumn?.dataType;

  return (
    <Flex
      key={`fieldMappingRow-${value.idColumn}`}
      withGap
      alignment="start"
      className={isInvalid ? 'pb-2' : ''}
      gapSize={'medium'}
      itemAlignment="center"
    >
      <Flex className={'w-5/12'}>
        <Select
          withSearch
          id={`import-mapping-select-field-${value.idColumn}-${id}`}
          options={listCustomField}
          value={option}
          onChange={handleOnChange}
        />
      </Flex>
      {selectMultiple ? (
        <Flex column className={`w-5/12 ${isInvalid ? 'relative' : ''}`} itemAlignment="start">
          <Select
            id={`import-mapping-select-format-${value.idColumn}-${id}`}
            options={selectDataType}
            value={selected}
            onChange={handleDataType}
          />
          {isInvalid && (
            <span className="absolute top-full text-rose-600">
              {t('IMPORT_MAIN.invalidFormat')}
            </span>
          )}
        </Flex>
      ) : (
        <></>
      )}
      <Flex noGrow className="w-2/12">
        <div className={'h-6 w-8'}>
          {value.mappingField.id === StateOption.noImport ? (
            <QuestionCircleIcon />
          ) : (
            <WarningDataMatch
              alreadyInUse={value.alreadyInUse}
              data={value.data}
              dataType={dataType ?? ''}
              format={format ?? ''}
              id={id}
            />
          )}
        </div>
      </Flex>

      {!selectMultiple && <Flex className="w-5/12" />}
    </Flex>
  );
}

export default memo(SelectRow);
